<script setup lang="ts">
/**
 * One group of the condition builder (internal): the combinator switch, its
 * rule rows and nested groups, and the add actions. Emits the whole changed
 * group; the builder replaces it in the tree.
 */
import { DeleteIcon } from '@blpde/nx-icons';
import Copy from 'lucide-vue-next/dist/esm/icons/copy.js'; import FolderPlus from 'lucide-vue-next/dist/esm/icons/folder-plus.js'; import Plus from 'lucide-vue-next/dist/esm/icons/plus.js';
import { computed, h, inject, type FunctionalComponent } from 'vue';
import { NxButton } from '../Button';
import { NxIconButton } from '../IconButton';
import { NxSingleSelect, NxTextInput } from '../Inputs';
import { conditionBuilderKey } from './context';
import {
  cloneConditionNode,
  createConditionGroup,
  createConditionRule,
  emptyValueFor,
  isConditionGroup,
  type NxConditionCombinator,
  type NxConditionGroup,
  type NxConditionNode,
  type NxConditionOperator,
  type NxConditionRule,
} from './model';

defineOptions({ name: 'NxConditionGroupNode' });

const props = defineProps<{
  group: NxConditionGroup;
  depth: number;
  /** Set on nested groups: shows remove / duplicate for the group itself. */
  removable?: boolean;
}>();

const emit = defineEmits<{
  update: [group: NxConditionGroup];
  remove: [];
  duplicate: [];
}>();

const ctx = inject(conditionBuilderKey)!;
const labels = ctx.labels;
const disabled = ctx.disabled;

const canNest = computed(() => props.depth + 1 < ctx.maxDepth.value);
const combinatorText = computed(() =>
  props.group.combinator === 'and' ? labels.value.and : labels.value.or
);

const fieldKeys = computed(() => ctx.fields.value.map((field) => field.key));

function fieldLabel(key: string): string {
  return ctx.fields.value.find((field) => field.key === key)?.label ?? key;
}
/** Group heading in the field dropdown. Empty means no heading. */
function fieldGroup(key: string): string | undefined {
  return ctx.fields.value.find((field) => field.key === key)?.group || undefined;
}

function fieldOf(rule: NxConditionRule) {
  return ctx.fields.value.find((f) => f.key === rule.field);
}
function operatorsOf(rule: NxConditionRule): NxConditionOperator[] {
  const field = fieldOf(rule);
  return field ? ctx.operators.value(field) : [];
}
function operatorKeys(rule: NxConditionRule): string[] {
  return operatorsOf(rule).map((operator) => operator.key);
}
function operatorText(rule: NxConditionRule): (key: string) => string {
  return (key) => operatorsOf(rule).find((operator) => operator.key === key)?.label ?? key;
}
function operatorOf(rule: NxConditionRule): NxConditionOperator | undefined {
  return operatorsOf(rule).find((o) => o.key === rule.op);
}

function replaceChild(index: number, next: NxConditionNode | null) {
  const children = [...props.group.children];
  if (next) children.splice(index, 1, next);
  else children.splice(index, 1);
  emit('update', { ...props.group, children });
}
function insertAfter(index: number, node: NxConditionNode) {
  const children = [...props.group.children];
  children.splice(index + 1, 0, node);
  emit('update', { ...props.group, children });
}
function setCombinator(combinator: NxConditionCombinator) {
  if (combinator !== props.group.combinator) emit('update', { ...props.group, combinator });
}
function addRule() {
  emit('update', { ...props.group, children: [...props.group.children, createConditionRule()] });
}
function addGroup() {
  const nested = createConditionGroup(props.group.combinator === 'and' ? 'or' : 'and', [
    createConditionRule(),
  ]);
  emit('update', { ...props.group, children: [...props.group.children, nested] });
}

function setField(index: number, rule: NxConditionRule, field: string) {
  const f = ctx.fields.value.find((x) => x.key === field);
  const first = f ? ctx.operators.value(f)[0] : undefined;
  replaceChild(index, {
    ...rule,
    field,
    op: first?.key ?? '',
    value: emptyValueFor(first?.arity),
  });
}
function setOperator(index: number, rule: NxConditionRule, op: string) {
  const before = operatorOf(rule);
  const after = operatorsOf(rule).find((o) => o.key === op);
  // Keep the value while the shape fits; start empty when the arity changes.
  const value = before?.arity === after?.arity ? rule.value : emptyValueFor(after?.arity);
  replaceChild(index, { ...rule, op, value });
}
function setValue(index: number, rule: NxConditionRule, value: unknown) {
  replaceChild(index, { ...rule, value });
}

function ruleName(rule: NxConditionRule, index: number): string {
  const field = fieldOf(rule);
  return field
    ? `${labels.value.rule} ${index + 1}: ${field.label}`
    : `${labels.value.rule} ${index + 1}`;
}

const listValue = (value: unknown) =>
  Array.isArray(value) ? value.join(', ') : String(value ?? '');
const parseList = (text: string) =>
  text
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
const rangePart = (value: unknown, i: 0 | 1) =>
  Array.isArray(value) ? String(value[i] ?? '') : i === 0 ? String(value ?? '') : '';

/** Renders the caller's `#value` slot for a rule, if there is one. */
const ValueOutlet: FunctionalComponent<{
  rule: NxConditionRule;
  index: number;
}> = (p) => {
  const slot = ctx.valueSlot();
  if (!slot) return null;
  return h(
    'div',
    { class: 'nx-condition__value-slot' },
    slot({
      rule: p.rule,
      field: fieldOf(p.rule),
      operator: operatorOf(p.rule),
      update: (value: unknown) => setValue(p.index, p.rule, value),
    })
  );
};
</script>

<template>
  <div
    class="nx-condition-group"
    :class="[`is-${props.group.combinator}`, { 'is-nested': props.depth > 0 }]"
    role="group"
    :aria-label="`${labels.group} (${combinatorText})`"
  >
    <div class="nx-condition-group__head">
      <div class="nx-condition-group__switch" role="radiogroup" :aria-label="labels.combinator">
        <button
          v-for="c in ['and', 'or'] as const"
          :key="c"
          type="button"
          role="radio"
          class="nx-condition-group__combinator"
          :class="{ 'is-on': props.group.combinator === c }"
          :aria-checked="props.group.combinator === c"
          :disabled="disabled"
          @click="setCombinator(c)"
        >
          {{ c === 'and' ? labels.and : labels.or }}
        </button>
      </div>
      <span class="nx-condition-group__spacer" />
      <template v-if="props.removable && !disabled">
        <NxIconButton
          height="xs"
          :title="labels.duplicate"
          :aria-label="`${labels.group}: ${labels.duplicate}`"
          @click="emit('duplicate')"
        >
          <Copy class="nxt-icon-sm" />
        </NxIconButton>
        <NxIconButton
          height="xs"
          danger
          :title="labels.remove"
          :aria-label="`${labels.group}: ${labels.remove}`"
          @click="emit('remove')"
        >
          <DeleteIcon class="nxt-icon-sm" />
        </NxIconButton>
      </template>
    </div>

    <ol class="nx-condition-group__items">
      <li
        v-for="(child, index) in props.group.children"
        :key="child.id"
        class="nx-condition-group__item"
      >
        <span v-if="index > 0" class="nx-condition-group__joint" aria-hidden="true">
          {{ combinatorText }}
        </span>
        <NxConditionGroupNode
          v-if="isConditionGroup(child)"
          :group="child"
          :depth="props.depth + 1"
          removable
          @update="replaceChild(index, $event)"
          @remove="replaceChild(index, null)"
          @duplicate="insertAfter(index, cloneConditionNode(child))"
        />
        <div
          v-else
          class="nx-condition-rule"
          role="group"
          :aria-label="ruleName(child, index)"
          data-testid="nx-condition-rule"
        >
          <NxSingleSelect
            class="nx-condition-rule__field"
            height="s"
            fit-dropdown-width
            :model-value="child.field || null"
            :options="fieldKeys"
            :option-text="fieldLabel"
            :option-group="fieldGroup"
            :placeholder="labels.fieldPlaceholder"
            :disabled="disabled"
            :trigger-aria-label="labels.field"
            @update:model-value="setField(index, child, String($event ?? ''))"
          />
          <NxSingleSelect
            class="nx-condition-rule__op"
            height="s"
            fit-dropdown-width
            :model-value="child.op || null"
            :options="operatorKeys(child)"
            :option-text="operatorText(child)"
            :placeholder="labels.operator"
            :disabled="disabled || !child.field"
            :trigger-aria-label="labels.operator"
            @update:model-value="setOperator(index, child, String($event ?? ''))"
          />
          <div class="nx-condition-rule__value">
            <template v-if="operatorOf(child)?.arity !== 0 && child.op">
              <ValueOutlet v-if="ctx.valueSlot()" :rule="child" :index="index" />
              <div v-else-if="operatorOf(child)?.arity === 2" class="nx-condition-rule__range">
                <NxTextInput
                  height="s"
                  :model-value="rangePart(child.value, 0)"
                  :disabled="disabled"
                  :aria-label="labels.valueFrom"
                  :placeholder="labels.valueFrom"
                  @update:model-value="setValue(index, child, [$event, rangePart(child.value, 1)])"
                />
                <span aria-hidden="true">–</span>
                <NxTextInput
                  height="s"
                  :model-value="rangePart(child.value, 1)"
                  :disabled="disabled"
                  :aria-label="labels.valueTo"
                  :placeholder="labels.valueTo"
                  @update:model-value="setValue(index, child, [rangePart(child.value, 0), $event])"
                />
              </div>
              <NxTextInput
                height="s"
                v-else-if="operatorOf(child)?.arity === 'many'"
                :model-value="listValue(child.value)"
                :disabled="disabled"
                :aria-label="labels.valueList"
                :placeholder="labels.valueList"
                @update:model-value="setValue(index, child, parseList($event))"
              />
              <NxTextInput
                height="s"
                v-else
                :model-value="String(child.value ?? '')"
                :disabled="disabled"
                :aria-label="labels.value"
                :placeholder="labels.value"
                @update:model-value="setValue(index, child, $event)"
              />
            </template>
          </div>
          <div v-if="!disabled" class="nx-condition-rule__actions">
            <NxIconButton
              height="xs"
              :title="labels.duplicate"
              :aria-label="`${ruleName(child, index)}: ${labels.duplicate}`"
              @click="insertAfter(index, cloneConditionNode(child))"
            >
              <Copy class="nxt-icon-sm" />
            </NxIconButton>
            <NxIconButton
              height="xs"
              danger
              :title="labels.remove"
              :aria-label="`${ruleName(child, index)}: ${labels.remove}`"
              data-testid="nx-condition-remove"
              @click="replaceChild(index, null)"
            >
              <DeleteIcon class="nxt-icon-sm" />
            </NxIconButton>
          </div>
        </div>
      </li>
    </ol>

    <p v-if="!props.group.children.length" class="nx-condition-group__empty">{{ labels.empty }}</p>

    <div v-if="!disabled" class="nx-condition-group__add">
      <NxButton height="xs" variant="ghost" data-testid="nx-condition-add-rule" @click="addRule">
        <Plus class="nxt-icon-sm" aria-hidden="true" />
        {{ labels.addRule }}
      </NxButton>
      <NxButton
        v-if="canNest"
        height="xs"
        variant="ghost"
        data-testid="nx-condition-add-group"
        @click="addGroup"
      >
        <FolderPlus class="nxt-icon-sm" aria-hidden="true" />
        {{ labels.addGroup }}
      </NxButton>
    </div>
  </div>
</template>

<style scoped>
.nx-condition-group {
  --rail: var(--nxt-brand-bg);
  --rail-soft: var(--nxt-brand-subtle-bg);
  --rail-fg: var(--nxt-brand-subtle-fg);

  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-xs);
  padding: var(--nxt-space-sm) var(--nxt-space-sm) var(--nxt-space-xs) var(--nxt-space-md);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-lg);
  background: var(--nxt-surface-bg);
  box-shadow: inset calc(var(--nxt-hairline) * 3) 0 0 var(--rail);
}
.nx-condition-group.is-or {
  --rail: var(--nxt-hue-violet);
  --rail-soft: var(--nxt-hue-violet-subtle-bg);
  --rail-fg: var(--nxt-hue-violet-fg);
}
.nx-condition-group.is-nested {
  background: var(--nxt-surface-sunken-bg);
}
.nx-condition-group__head {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
}
.nx-condition-group__spacer {
  flex: 1 1 auto;
}
.nx-condition-group__switch {
  display: inline-flex;
  padding: calc(var(--nxt-hairline) * 2);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-surface-sunken-bg);
}
.nx-condition-group.is-nested .nx-condition-group__switch {
  background: var(--nxt-surface-bg);
}
.nx-condition-group__combinator {
  padding: 0 var(--nxt-space-sm);
  border: 0;
  border-radius: var(--nxt-radius-full);
  background: transparent;
  color: var(--nxt-fg-muted);
  font: inherit;
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
  letter-spacing: 0.04em;
  line-height: var(--nxt-control-size-xs);
  cursor: pointer;
  transition:
    background-color var(--nxt-motion-fast) var(--nxt-ease-out),
    color var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-condition-group__combinator.is-on {
  background: var(--rail);
  color: var(--nxt-fg-on-accent);
}
.nx-condition-group__combinator:focus-visible {
  outline: none;
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
.nx-condition-group__combinator:disabled {
  cursor: default;
}
.nx-condition-group__items {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
  margin: 0;
  padding: 0;
  list-style: none;
}
.nx-condition-group__item {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
}
.nx-condition-group__joint {
  align-self: flex-start;
  margin-inline-start: var(--nxt-space-sm);
  padding: 0 var(--nxt-space-xs);
  border-radius: var(--nxt-radius-full);
  background: var(--rail-soft);
  color: var(--rail-fg);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
  letter-spacing: 0.04em;
  line-height: 1.6;
}
.nx-condition-rule {
  display: grid;
  grid-template-columns:
    minmax(calc(var(--nxt-size-unit) * 8), 1fr) minmax(calc(var(--nxt-size-unit) * 6), 0.8fr)
    minmax(calc(var(--nxt-size-unit) * 8), 1.4fr) auto;
  align-items: center;
  gap: var(--nxt-space-xs);
  min-width: 0;
}
.nx-condition-rule__field,
.nx-condition-rule__op {
  min-width: 0;
}
@container (max-width: 560px) {
  .nx-condition-rule {
    grid-template-columns: 1fr 1fr auto;
  }
  .nx-condition-rule__value {
    grid-column: 1 / -2;
  }
}
.nx-condition-rule__value {
  min-width: 0;
}
.nx-condition-rule__range {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  color: var(--nxt-fg-muted);
}
.nx-condition-rule__actions {
  display: inline-flex;
  gap: var(--nxt-space-2xs);
}
.nx-condition-group__empty {
  margin: 0;
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-sm);
}
.nx-condition-group__add {
  display: flex;
  flex-wrap: wrap;
  gap: var(--nxt-space-2xs);
}
@media (prefers-reduced-motion: reduce) {
  .nx-condition-group__combinator {
    transition: none;
  }
}
</style>
